import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { customerApi as api, authApi } from '../services/api'
import { PlusIcon, ChatBubbleLeftRightIcon, UserGroupIcon, ArrowPathIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline'
import AgentMyLeads from '../components/agent-leads/AgentMyLeads'
import AgentAddLeadModal from '../components/agent-leads/AgentAddLeadModal'

// IMPORTANT: handover routes live under /api/*, NOT /api/v1/*. The default
// `api` instance in services/api.js points at /api/v1/ (admin-side), so
// customer+agent calls must go through `customerApi`/`authApi` which point
// at /api/. The SSE URL is derived from authApi so dev + prod both work.
const API_PREFIX = (authApi.defaults?.baseURL || '/api').replace(/\/+$/, '')

export default function HandoverAgentConsole() {
  const { user, logout } = useAuth()
  const [summary, setSummary] = useState(null)
  const [queue, setQueue] = useState({ waiting: [], mine: [], team_active: [] })
  const [liveChats, setLiveChats] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [lastRefresh, setLastRefresh] = useState(null)
  const [openHandoverId, setOpenHandoverId] = useState(null)
  const [openDetail, setOpenDetail] = useState(null)
  const [draftMsg, setDraftMsg] = useState('')
  const [sending, setSending] = useState(false)
  const esRef = useRef(null)
  const openIdRef = useRef(null)

  // Tab + add-lead-modal state (new in this redesign)
  const [activeTab, setActiveTab] = useState('live')  // 'live' | 'leads'
  const [addLeadOpen, setAddLeadOpen] = useState(false)
  // Toast after a lead save — small, auto-dismiss, non-blocking.
  const [toast, setToast] = useState(null)

  const loadStats = useCallback(async () => {
    setError('')
    try {
      const [s, q, lc] = await Promise.all([
        api.get('/agent/dashboard/summary'),
        api.get('/agent/queue'),
        api.get('/agent/queue/live-chats').catch(() => ({ data: { chats: [] } })),
      ])
      setSummary(s.data)
      setQueue(q.data)
      setLiveChats(lc.data?.chats || [])
      setLastRefresh(new Date())
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load handover queue.')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadDetail = useCallback(async (handoverId) => {
    try {
      const r = await api.get(`/agent/handovers/${handoverId}`)
      setOpenDetail(r.data)
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load conversation.')
    }
  }, [])

  const claim = async (handoverId) => {
    try {
      const r = await api.post(`/agent/handovers/${handoverId}/claim`)
      setOpenHandoverId(handoverId)
      setOpenDetail(r.data)
      loadStats()
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not claim handover.')
    }
  }

  const decline = async (handoverId) => {
    if (!window.confirm("Decline this handover? The visitor will be told no agent is available and the bot will resume.")) return
    try {
      await api.post(`/agent/handovers/${handoverId}/decline`, { reason: 'declined by agent' })
      loadStats()
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not decline handover.')
    }
  }

  // Proactive takeover from the Live Chats panel — grabs a bot-handled
  // conversation and pulls it into the agent's "My Active" column.
  const takeover = async (conversationOrItem) => {
    const conversationId = typeof conversationOrItem === 'string'
      ? conversationOrItem
      : conversationOrItem.conversation_id
    // Warn when the conversation is stale — the visitor is likely gone
    // already, so joining it just creates idle inventory.
    const lastMsgIso = (conversationOrItem && conversationOrItem.last_message_at) || null
    const ageMin = lastMsgIso ? (Date.now() - new Date(lastMsgIso).getTime()) / 60000 : 0
    let prompt = 'Take over this chat? The bot will stop replying and the visitor will see your messages.'
    if (ageMin > 30) {
      prompt = `Heads up — the visitor's last activity was ${Math.round(ageMin)} min ago; they may have closed the widget. Take over anyway?`
    }
    if (!window.confirm(prompt)) return
    try {
      const r = await api.post('/agent/handovers/takeover', { conversation_id: conversationId, reason: 'agent proactively joined' })
      setOpenHandoverId(r.data.handover_id)
      setOpenDetail(r.data)
      loadStats()
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not take over this chat.')
    }
  }

  const sendMessage = async () => {
    const content = draftMsg.trim()
    if (!content || !openHandoverId) return
    setSending(true)
    try {
      const r = await api.post(`/agent/handovers/${openHandoverId}/messages`, { content })
      setOpenDetail(r.data)
      setDraftMsg('')
    } catch (e) {
      alert(e?.response?.data?.detail || 'Send failed.')
    } finally {
      setSending(false)
    }
  }

  const endHandover = async () => {
    if (!openHandoverId) return
    if (!window.confirm('End this handover? The bot will resume for the visitor.')) return
    try {
      await api.post(`/agent/handovers/${openHandoverId}/end`)
      setOpenHandoverId(null)
      setOpenDetail(null)
      loadStats()
    } catch (e) {
      alert(e?.response?.data?.detail || 'End failed.')
    }
  }

  // Live updates — we run SSE AND 5s polling in parallel. If the ingress
  // (Azure Container Apps / nginx) buffers SSE, polling catches the state.
  // SSE still provides instant updates when the stream is flowing. Both
  // paths call loadStats() which is idempotent.
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) return

    // 1) SSE for instant updates
    const url = `${API_PREFIX}/agent/stream?token=${encodeURIComponent(token)}`
    const es = new EventSource(url)
    esRef.current = es

    const onLifecycle = () => loadStats()
    es.addEventListener('handover.requested', onLifecycle)
    es.addEventListener('handover.claimed', onLifecycle)
    es.addEventListener('handover.takeover', onLifecycle)
    const onCloseEvent = (evt) => {
      loadStats()
      try {
        const d = JSON.parse(evt.data)
        if (openIdRef.current && d.handover_id === openIdRef.current) {
          setOpenHandoverId(null); setOpenDetail(null)
        }
      } catch {}
    }
    es.addEventListener('handover.ended', onCloseEvent)
    es.addEventListener('handover.declined', onCloseEvent)
    const onMessage = (evt) => {
      try {
        const d = JSON.parse(evt.data)
        if (openIdRef.current && d.handover_id === openIdRef.current) {
          loadDetail(openIdRef.current)
        }
        loadStats()
      } catch {}
    }
    es.addEventListener('handover.agent_message', onMessage)
    es.addEventListener('handover.visitor_message', onMessage)
    // Visitor-presence lifecycle events (fired on tab close / refresh
    // and again when the visitor returns). We refresh the detail
    // payload so visitor_left_at / visitor_last_seen_at update, and
    // we push a transient system notice into the chat view so the
    // agent sees exactly what happened without having to infer from
    // a timestamp.
    const onPresence = (evt) => {
      try {
        const d = JSON.parse(evt.data)
        if (openIdRef.current && d.handover_id === openIdRef.current) {
          loadDetail(openIdRef.current)
        }
      } catch {}
    }
    es.addEventListener('handover.visitor_disconnected', onPresence)
    es.addEventListener('handover.visitor_returned', onPresence)
    es.onerror = () => {
      // EventSource auto-reconnects on its own after a brief backoff.
    }

    // 2) Polling fallback — runs regardless of SSE health. 5s cadence is
    // responsive enough for a human agent and cheap for the backend.
    const pollTimer = setInterval(() => {
      loadStats()
      if (openIdRef.current) loadDetail(openIdRef.current)
    }, 5000)

    loadStats() // initial load

    return () => {
      try { es.close() } catch {}
      clearInterval(pollTimer)
      esRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { openIdRef.current = openHandoverId }, [openHandoverId])

  // Server timestamps are UTC. Older/other endpoints historically returned
  // naive datetimes (no 'Z' suffix) which the browser would then parse as
  // LOCAL time, giving "5h ago" bugs in UTC+5:30 timezones. We append 'Z'
  // defensively if the string looks like a naive ISO datetime.
  const parseServerTime = (iso) => {
    if (iso instanceof Date) return iso
    if (!iso || typeof iso !== 'string') return null
    const hasTz = /Z$|[+-]\d\d:?\d\d$/.test(iso)
    return new Date(hasTz ? iso : iso + 'Z')
  }

  const fmtTime = (iso) => {
    if (!iso) return '—'
    const d = parseServerTime(iso)
    if (!d || isNaN(d.getTime())) return '—'
    const diffMs = Date.now() - d.getTime()
    if (diffMs < 60_000) return 'just now'
    if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m ago`
    if (diffMs < 86_400_000) return `${Math.floor(diffMs / 3_600_000)}h ago`
    return d.toLocaleString()
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600" />
      </div>
    )
  }

  const openItem = (handoverId) => {
    setOpenHandoverId(handoverId)
    loadDetail(handoverId)
  }

  // Pre-fill for the Add-Lead modal. If the agent currently has a chat
  // open, use the visitor's name + handover id; otherwise blank.
  const addLeadPrefill = openDetail
    ? {
        visitor_name: openDetail.visitor_name || '',
        handover_id: openHandoverId,
      }
    : {}

  const onLeadSaved = (lead) => {
    setToast({ kind: 'success', msg: `Saved lead for ${lead?.visitor_name || 'visitor'}` })
    setTimeout(() => setToast(null), 3500)
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* ─── Top app bar: brand, agent identity, actions ─── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-600 to-indigo-700 text-white flex items-center justify-center font-bold text-sm shadow-sm">
              HA
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-slate-900 leading-tight">
                Handover Console
              </h1>
              <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                <span className="truncate">{user?.fullName}</span>
                {summary?.agent_display_id && (
                  <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-slate-600 shrink-0">
                    {summary.agent_display_id}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-emerald-600 shrink-0">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  live
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAddLeadOpen(true)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <PlusIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Add lead</span>
            </button>
            <button
              onClick={loadStats}
              title="Refresh"
              className="inline-flex items-center gap-1.5 px-2.5 py-2 text-sm text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => { logout(); window.location.assign('/login') }}
              title="Log out"
              className="inline-flex items-center gap-1.5 px-2.5 py-2 text-sm text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        {/* Tab bar — kept on its own row so it works cleanly on mobile */}
        <nav className="max-w-7xl mx-auto px-6 flex items-center gap-1 border-t border-slate-100">
          <TabButton
            active={activeTab === 'live'}
            onClick={() => setActiveTab('live')}
            icon={<ChatBubbleLeftRightIcon className="h-4 w-4" />}
            label="Live chats"
            count={(summary?.waiting_handovers || 0) + (summary?.my_active_handovers || 0)}
          />
          <TabButton
            active={activeTab === 'leads'}
            onClick={() => setActiveTab('leads')}
            icon={<UserGroupIcon className="h-4 w-4" />}
            label="My leads"
          />
          <span className="ml-auto text-[11px] text-slate-400 py-2 hidden sm:inline">
            Updated {fmtTime(lastRefresh)}
          </span>
        </nav>
      </header>

      {/* ─── Main — scrolls smoothly inside the viewport ─── */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
          {error && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          {activeTab === 'live' ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi label="Waiting" value={summary?.waiting_handovers ?? 0} tone="amber" />
                <Kpi label="My Active" value={summary?.my_active_handovers ?? 0} tone="indigo" />
                <Kpi label="Team Active" value={summary?.team_active_handovers ?? 0} tone="slate" />
                <Kpi label="Mine Today" value={summary?.my_handovers_today ?? 0} tone="emerald" />
                <Kpi label="Team Today" value={summary?.team_handovers_today ?? 0} tone="slate" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <WaitingQueueColumn
                  items={queue.waiting} fmtTime={fmtTime}
                  onAccept={(it) => claim(it.handover_id)}
                  onDecline={(it) => decline(it.handover_id)}
                />
                <QueueColumn
                  title="My Active" subtitle="Conversations you are handling right now"
                  emptyText="You are not in any handovers." tone="indigo"
                  items={queue.mine} fmtTime={fmtTime}
                  action="Open" onAction={(it) => openItem(it.handover_id)}
                />
                <QueueColumn
                  title="Team Active" subtitle="Handovers your teammates are handling"
                  emptyText="No teammates in active handovers." tone="slate"
                  items={queue.team_active} fmtTime={fmtTime}
                  action="View" onAction={(it) => openItem(it.handover_id)}
                />
              </div>

              {/* ─────────────── Live Chats — ALL ongoing conversations ───────────────
                  Shows every active widget conversation from the first message so
                  the agent can monitor and proactively take over, even when the
                  visitor hasn't explicitly asked for a human. */}
              <LiveChatsPanel
                chats={liveChats}
                fmtTime={fmtTime}
                parseServerTime={parseServerTime}
                onTakeover={(c) => takeover(c)}
                onOpen={(c) => c.handover_id && openItem(c.handover_id)}
              />
            </>
          ) : (
            <AgentMyLeads />
          )}
        </div>
      </main>

      {/* Add-lead modal — always available via the header button */}
      <AgentAddLeadModal
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        onSaved={onLeadSaved}
        prefill={addLeadPrefill}
      />

      {/* Toast — brief save confirmation */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`rounded-xl shadow-lg px-4 py-3 text-sm font-medium flex items-center gap-2 ${
            toast.kind === 'success'
              ? 'bg-emerald-600 text-white'
              : 'bg-rose-600 text-white'
          }`}>
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {toast.msg}
          </div>
        </div>
      )}

      {/* Chat drawer */}
      {openHandoverId && openDetail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end">
          <div className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                {/* Header identity line.
                    Priority: visitor_name (asked before handover OR
                    collected by lead-capture) → generic "Visitor".
                    We DO NOT show the raw visitor_id (a UUID-ish
                    anonymous token) alongside the name — that was
                    the explicit UX request: name only, no numbers. */}
                <div className="text-sm font-semibold text-slate-900">
                  {openDetail.visitor_name || 'Visitor'} ·{' '}
                  <span className={
                    openDetail.status === 'ended' ? 'text-slate-500' :
                    openDetail.status === 'active' || openDetail.status === 'claimed' ? 'text-emerald-600' :
                    'text-amber-600'
                  }>{openDetail.status}</span>
                </div>
                <div className="text-xs text-slate-500">
                  {openDetail.requested_by === 'agent' ? 'agent-initiated takeover' : 'visitor-requested'} ·{' '}
                  started {fmtTime(openDetail.requested_at)}
                </div>
              </div>
              <div className="flex gap-2">
                {openDetail.status !== 'ended' && openDetail.assigned_agent_id === summary?.agent_id && (
                  <button onClick={endHandover} className="text-sm px-3 py-1.5 rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50">End</button>
                )}
                <button onClick={() => { setOpenHandoverId(null); setOpenDetail(null) }} className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800">Close</button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
              {/* Hide the visitor-side system notes ("A human agent is
                  handling…" and "Still looking for an agent…") from the
                  AGENT's view. The agent already knows they're on the
                  line; repeating the note after every visitor turn is
                  pure clutter. We keep handover_request (useful: shows
                  the moment the visitor asked) and handover_unavailable
                  (useful: shows the handover ended by timeout). */}
              {(openDetail.messages || [])
                .filter((m) => m.agent_used !== 'handover_wait' && m.agent_used !== 'handover_waiting')
                .map((m) => {
                  const isUser = m.role === 'user'
                  const isHuman = m.agent_used === 'human_agent'
                  const isHandoverSys = m.agent_used === 'handover_request' || m.agent_used === 'handover_unavailable'
                  return (
                    <div key={m.id} className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                        isUser ? 'bg-white border border-slate-200 text-slate-900' :
                        isHuman ? 'bg-indigo-600 text-white' :
                        isHandoverSys ? 'bg-amber-100 text-amber-900 border border-amber-200' :
                        'bg-slate-200 text-slate-800'
                      }`}>
                        {m.content}
                        <div className={`mt-1 text-[10px] opacity-70 ${isUser ? 'text-slate-500' : ''}`}>
                          {isHuman ? (m.sender_display_id || 'agent') : (isUser ? 'visitor' : (m.agent_used || 'bot'))}
                          {' · '}{fmtTime(m.created_at)}
                        </div>
                      </div>
                    </div>
                  )
                })}
              {(openDetail.messages || []).filter((m) => m.agent_used !== 'handover_wait' && m.agent_used !== 'handover_waiting').length === 0 && (
                <div className="text-center text-sm text-slate-400 py-10">No messages yet.</div>
              )}
              {/* Visitor-presence inline notice. Pinned to the bottom
                  of the thread so the agent sees the current state
                  (left / returned / stale heartbeat) without having
                  to read the composer banner below. Driven entirely
                  by payload timestamps so a page refresh preserves
                  the state. */}
              {(() => {
                const leftIso = openDetail.visitor_left_at
                const lastIso = openDetail.visitor_last_seen_at
                const leftMs = leftIso ? (parseServerTime(leftIso)?.getTime() || 0) : 0
                const seenMs = lastIso ? (parseServerTime(lastIso)?.getTime() || 0) : 0
                const nowMs = Date.now()
                const STALE_MS = 60 * 1000
                // "Left" wins if the explicit beacon is more recent
                // than the last heartbeat (visitor fired pagehide and
                // hasn't come back). The heartbeat endpoint clears
                // visitor_left_at on return, so once they're back
                // leftMs will be 0 or older than seenMs.
                const showLeft = openDetail.status !== 'ended' && leftMs > 0 && leftMs > seenMs
                const showReturned = openDetail.status !== 'ended' &&
                  seenMs > 0 && leftMs > 0 && seenMs > leftMs &&
                  (nowMs - seenMs) < 60 * 1000
                const showStale = openDetail.status !== 'ended' && !showLeft &&
                  seenMs > 0 && (nowMs - seenMs) > STALE_MS
                if (showLeft) {
                  const reason = openDetail.visitor_left_reason === 'refresh'
                    ? 'refreshed the page'
                    : openDetail.visitor_left_reason === 'hidden'
                      ? 'switched away from the tab'
                      : 'closed the chat window'
                  return (
                    <div className="my-2 flex justify-center">
                      <div className="text-[11px] px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                        Visitor left the chat ({reason}) · {fmtTime(leftIso)}
                      </div>
                    </div>
                  )
                }
                if (showReturned) {
                  return (
                    <div className="my-2 flex justify-center">
                      <div className="text-[11px] px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        Visitor is back · {fmtTime(lastIso)}
                      </div>
                    </div>
                  )
                }
                if (showStale) {
                  const sinceSec = Math.floor((nowMs - seenMs) / 1000)
                  const sinceLabel = sinceSec < 60 ? `${sinceSec}s` : `${Math.floor(sinceSec / 60)}m`
                  return (
                    <div className="my-2 flex justify-center">
                      <div className="text-[11px] px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        Visitor appears away · last seen {sinceLabel} ago
                      </div>
                    </div>
                  )
                }
                return null
              })()}
            </div>

            {/* composer with presence + idle detection */}
            {(() => {
              const isMine = openDetail.status !== 'ended' && openDetail.assigned_agent_id === summary?.agent_id
              const msgs = openDetail.messages || []
              const lastVisitorMsg = [...msgs].reverse().find((m) => m.role === 'user')
              const lastVisitorMs = lastVisitorMsg ? (parseServerTime(lastVisitorMsg.created_at)?.getTime() || 0) : 0
              const claimedMs = openDetail.claimed_at ? (parseServerTime(openDetail.claimed_at)?.getTime() || 0) : 0
              // Fast disconnect signal — three ways we know:
              //   1. explicit visitor_left_at beacon (pagehide /
              //      beforeunload) → instant
              //   2. visitor_last_seen heartbeat >60s old → within 60s
              //   3. the client never heard a heartbeat (older widget)
              //      → fall through to idle fallback below.
              const lastSeenMs = openDetail.visitor_last_seen_at
                ? (parseServerTime(openDetail.visitor_last_seen_at)?.getTime() || 0)
                : 0
              const leftAtMs = openDetail.visitor_left_at
                ? (parseServerTime(openDetail.visitor_left_at)?.getTime() || 0)
                : 0
              const DISCONNECT_LIMIT = 60 * 1000
              const sinceSeenMs = lastSeenMs ? (Date.now() - lastSeenMs) : Infinity
              // "Left" wins if the explicit beacon is more recent
              // than the heartbeat (they fired pagehide after their
              // last heartbeat and haven't come back since).
              const explicitLeft = isMine && leftAtMs > 0 && leftAtMs > lastSeenMs
              const visitorOffline = explicitLeft ||
                (isMine && lastSeenMs > 0 && sinceSeenMs > DISCONNECT_LIMIT)

              // Idle fallback — only kicks in if we never got a heartbeat
              // (older widget version pre-heartbeat). Baseline = later of
              // (claim time, last visitor msg) so a fresh takeover gives
              // the agent a full 15-min window.
              const baselineMs = Math.max(lastVisitorMs, claimedMs)
              const idleMs = baselineMs ? (Date.now() - baselineMs) : 0
              const IDLE_LIMIT = 15 * 60 * 1000
              const idle = isMine && !lastSeenMs && baselineMs > 0 && idleMs > IDLE_LIMIT
              const idleFromClaim = idle && claimedMs >= lastVisitorMs

              if (openDetail.status === 'ended') {
                return <div className="p-3 border-t bg-slate-100 text-xs text-slate-500">This handover has ended — the bot has resumed for the visitor.</div>
              }
              if (!isMine) {
                return (
                  <div className="p-3 border-t bg-amber-50 text-xs text-amber-700">
                    Read-only — {openDetail.assigned_agent_display_id || 'a teammate'} is handling this conversation.
                  </div>
                )
              }
              if (visitorOffline) {
                // Pick the most informative "when" to show. If we have
                // an explicit visitor_left_at, use that (the visitor
                // actively left); otherwise fall back to "last seen
                // Xs ago" from the heartbeat.
                let bannerText
                if (explicitLeft) {
                  const reason = openDetail.visitor_left_reason === 'refresh'
                    ? 'refreshed the page'
                    : openDetail.visitor_left_reason === 'hidden'
                      ? 'switched tabs'
                      : 'closed the chat window'
                  bannerText = `Visitor left the chat (${reason}). Composer disabled.`
                } else {
                  const sinceSec = Math.floor(sinceSeenMs / 1000)
                  const sinceLabel = sinceSec < 60 ? `${sinceSec}s` : `${Math.floor(sinceSec / 60)}m`
                  bannerText = `Visitor disconnected — last seen ${sinceLabel} ago (closed the widget or navigated away). Composer disabled.`
                }
                return (
                  <div className="p-3 border-t border-slate-200 bg-rose-50">
                    <div className="text-xs text-rose-700 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                      {bannerText}
                    </div>
                    <button
                      onClick={endHandover}
                      className="mt-2 text-xs px-3 py-1.5 rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50"
                    >
                      End handover
                    </button>
                  </div>
                )
              }
              if (idle) {
                const idleMin = Math.floor(idleMs / 60000)
                return (
                  <div className="p-3 border-t border-slate-200 bg-slate-50">
                    <div className="text-xs text-slate-600 flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                      {idleFromClaim
                        ? `No visitor reply for ${idleMin} min after you joined — they may have closed the widget. Composer disabled.`
                        : `Visitor has been idle for ${idleMin} min — may have closed the widget. Composer disabled.`}
                    </div>
                    <button
                      onClick={endHandover}
                      className="mt-2 text-xs px-3 py-1.5 rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50"
                    >
                      End handover
                    </button>
                  </div>
                )
              }
              return (
                <div className="p-3 border-t border-slate-200 bg-white">
                  <div className="flex gap-2">
                    <input
                      value={draftMsg} onChange={(e) => setDraftMsg(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() } }}
                      placeholder="Type a message to the visitor…"
                      className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded"
                      disabled={sending}
                    />
                    <button onClick={sendMessage} disabled={sending || !draftMsg.trim()} className="px-4 py-2 text-sm rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                      {sending ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Enter to send — the bot stays silent while you reply.</p>
                </div>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}

function Kpi({ label, value, tone }) {
  const tones = {
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
  }
  return (
    <div className={`rounded-xl border p-4 transition-all hover:shadow-sm ${tones[tone] || tones.slate}`}>
      <div className="text-xs uppercase tracking-wide opacity-80">{label}</div>
      <div className="mt-1 text-3xl font-semibold">{value}</div>
    </div>
  )
}

function TabButton({ active, onClick, icon, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`relative inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors ${
        active
          ? 'text-indigo-700'
          : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      {icon}
      <span>{label}</span>
      {typeof count === 'number' && count > 0 && (
        <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-semibold ${
          active ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-700'
        }`}>
          {count}
        </span>
      )}
      {active && (
        <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-indigo-600 rounded-t" />
      )}
    </button>
  )
}

// Specialised "Waiting" column: shows BOTH an Accept and a Decline button
// per row so the agent can either take the handover or tell the visitor
// no agent is available (which resumes the bot).
function WaitingQueueColumn({ items, fmtTime, onAccept, onDecline }) {
  return (
    <section className="rounded-xl border bg-white border-amber-200 overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-slate-200">
        <div className="text-sm font-semibold text-amber-700">Waiting</div>
        <div className="text-xs text-slate-500">
          Visitors asking for a human — <span className="font-medium">accept</span> to take it,{' '}
          <span className="font-medium">decline</span> to let the bot resume.
        </div>
      </div>
      <div className="flex-1 overflow-y-auto max-h-[70vh] divide-y divide-slate-100">
        {items.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No one is waiting. Nice.</div>
        ) : items.map((it) => (
          <div key={it.handover_id} className="p-3 hover:bg-slate-50">
            <div className="min-w-0">
              <div className="text-sm text-slate-900 truncate">
                {it.first_message || <em className="text-slate-400">No preview</em>}
              </div>
              <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                <span>{fmtTime(it.requested_at)}</span>
                <span>·</span>
                <span>{it.message_count} msgs</span>
                {it.requested_by === 'agent' && (
                  <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5">agent-initiated</span>
                )}
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => onAccept(it)}
                className="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Accept
              </button>
              <button
                onClick={() => onDecline(it)}
                className="text-xs px-3 py-1 rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50"
              >
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function QueueColumn({ title, subtitle, emptyText, tone, items, fmtTime, action, onAction }) {
  const borders = { amber: 'border-amber-200', indigo: 'border-indigo-200', slate: 'border-slate-200' }
  const headerTone = { amber: 'text-amber-700', indigo: 'text-indigo-700', slate: 'text-slate-700' }
  return (
    <section className={`rounded-xl border bg-white ${borders[tone]} overflow-hidden flex flex-col`}>
      <div className="px-4 py-3 border-b border-slate-200">
        <div className={`text-sm font-semibold ${headerTone[tone]}`}>{title}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
      </div>
      <div className="flex-1 overflow-y-auto max-h-[70vh] divide-y divide-slate-100">
        {items.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">{emptyText}</div>
        ) : (
          items.map((it) => (
            <div key={it.handover_id} className="p-3 hover:bg-slate-50">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900 truncate">
                    {it.first_message || <em className="text-slate-400">No preview</em>}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                    <span>{fmtTime(it.requested_at)}</span>
                    <span>·</span>
                    <span>{it.message_count} msgs</span>
                    {it.requested_by === 'agent' && (
                      <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5">agent-initiated</span>
                    )}
                    {it.assigned_agent_display_id && (
                      <span className="inline-flex items-center rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-700">
                        {it.assigned_agent_display_id}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => onAction(it)} className="text-xs px-2.5 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 flex-shrink-0">
                  {action}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  )
}

// Live Chats panel: every ongoing widget conversation for this tenant,
// not just handover-triggered ones. "In Handover" badge shows which chats
// already have a human on them; "Take Over" lets the agent proactively
// pull a bot-only chat into their My Active column.
function LiveChatsPanel({ chats, fmtTime, onTakeover, onOpen, parseServerTime }) {
  // "Fresh" = last activity within 15 min. For stale non-handover rows
  // we deliberately offer no "Take over" (the visitor almost certainly
  // closed the widget hours ago). The row stays visible so the agent
  // can skim history, but the only action is open-to-view the chat
  // (which falls back to the handover drawer when there is one).
  const RECENT_LIMIT = 15 * 60 * 1000
  return (
    <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Live Chats</div>
          <div className="text-xs text-slate-500">Every chat on your widget. Take over new ones, view older ones.</div>
        </div>
        <span className="text-xs text-slate-500">{chats.length} total</span>
      </div>
      <div className="divide-y divide-slate-100 max-h-[50vh] overflow-y-auto">
        {chats.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No chats in the last 24 hours.</div>
        ) : chats.map((c) => {
          const inHandover = !!c.handover_id
          const latestFromAgent = c.latest_agent_used === 'human_agent'
          const lastMs = c.last_message_at ? (parseServerTime(c.last_message_at)?.getTime() || 0) : 0
          const isRecent = lastMs > 0 && (Date.now() - lastMs) < RECENT_LIMIT
          return (
            <div key={c.conversation_id} className="p-3 hover:bg-slate-50 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900 truncate">
                  {c.first_message || <em className="text-slate-400">no preview</em>}
                </div>
                <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                  <span>last {fmtTime(c.last_message_at)}</span>
                  <span>·</span>
                  <span>{c.message_count} msgs</span>
                  {c.latest_role === 'user' && (
                    <span className="inline-flex items-center rounded bg-amber-50 px-1.5 py-0.5 text-amber-800">visitor spoke last</span>
                  )}
                  {latestFromAgent && (
                    <span className="inline-flex items-center rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-700">agent replied</span>
                  )}
                  {inHandover && (
                    <span className="inline-flex items-center rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">
                      {c.handover_status}{c.assigned_agent_display_id ? ' · ' + c.assigned_agent_display_id : ''}
                    </span>
                  )}
                  {!inHandover && !isRecent && (
                    <span className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">closed</span>
                  )}
                </div>
              </div>
              {inHandover ? (
                <button onClick={() => onOpen(c)} className="text-xs px-2.5 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50">
                  Open
                </button>
              ) : isRecent ? (
                <button onClick={() => onTakeover(c)} className="text-xs px-2.5 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700">
                  Take over
                </button>
              ) : (
                <span className="text-xs text-slate-400">—</span>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
